
import { useState, useRef, useMemo } from 'react';
import type { FinanceRecord } from '../../../store/financeStore';
import type { Customer } from '../../../store/customerStore';

interface Props {
  open: boolean;
  onClose: () => void;
  records: FinanceRecord[];
  customers: Customer[];
}

export default function CustomerReportModal({ open, onClose, records, customers }: Props) {
  const [filterType, setFilterType] = useState<'all' | '供应商' | '客户'>('all');
  const [exportingPDF, setExportingPDF] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const customerStats = useMemo(() => {
    const map: Record<string, {
      name: string; type: string; contact: string; phone: string; address: string;
      purchaseQty: number; purchaseAmt: number; purchaseCount: number;
      salesQty: number; salesAmt: number; salesCount: number;
      lastDate: string; varieties: Set<string>;
    }> = {};

    records.forEach(r => {
      const key = r.counterpart || r.source || '未知';
      if (!map[key]) {
        const cust = customers.find(c => c.name === key);
        map[key] = {
          name: key,
          type: cust?.type || (r.bizType === '收购' ? '供应商' : '客户'),
          contact: cust?.contact || r.contact || '',
          phone: cust?.phone || r.phone || '',
          address: cust?.address || '',
          purchaseQty: 0, purchaseAmt: 0, purchaseCount: 0,
          salesQty: 0, salesAmt: 0, salesCount: 0,
          lastDate: '', varieties: new Set(),
        };
      }
      const c = map[key];
      c.varieties.add(r.variety);
      if (r.date > c.lastDate) c.lastDate = r.date;
      if (r.bizType === '收购') {
        c.purchaseQty += r.quantity;
        c.purchaseAmt += r.amount;
        c.purchaseCount++;
      } else {
        c.salesQty += r.quantity;
        c.salesAmt += r.amount;
        c.salesCount++;
      }
    });

    let list = Object.values(map).map(c => ({
      ...c,
      totalAmt: c.purchaseAmt + c.salesAmt,
      totalQty: c.purchaseQty + c.salesQty,
      totalCount: c.purchaseCount + c.salesCount,
      varietyList: Array.from(c.varieties).join('、'),
    }));

    if (filterType !== 'all') {
      list = list.filter(c => c.type === filterType);
    }

    list.sort((a, b) => b.totalAmt - a.totalAmt);
    return list;
  }, [records, customers, filterType]);

  const totals = useMemo(() => customerStats.reduce((acc, c) => ({
    purchaseQty: acc.purchaseQty + c.purchaseQty,
    purchaseAmt: acc.purchaseAmt + c.purchaseAmt,
    salesQty: acc.salesQty + c.salesQty,
    salesAmt: acc.salesAmt + c.salesAmt,
    totalAmt: acc.totalAmt + c.totalAmt,
    totalCount: acc.totalCount + c.totalCount,
  }), { purchaseQty: 0, purchaseAmt: 0, salesQty: 0, salesAmt: 0, totalAmt: 0, totalCount: 0 }), [customerStats]);

  const handleExportPDF = async () => {
    if (!contentRef.current) return;
    setExportingPDF(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(contentRef.current, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW - 20;
      const imgH = (canvas.height * imgW) / canvas.width;
      let hLeft = imgH;
      let pos = 10;
      pdf.addImage(imgData, 'PNG', 10, pos, imgW, imgH);
      hLeft -= pdfH - 20;
      while (hLeft > 0) { pos = hLeft - imgH + 10; pdf.addPage(); pdf.addImage(imgData, 'PNG', 10, pos, imgW, imgH); hLeft -= pdfH - 20; }
      pdf.save(`客户往来统计表_${filterType === 'all' ? '全部' : filterType}.pdf`);
    } catch { window.print(); } finally { setExportingPDF(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[1100px] max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* 头部 */}
        <div className="flex items-center justify-between px-8 pt-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center">
              <i className="ri-team-line text-xl text-sky-600"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">客户往来统计表</h2>
              <p className="text-xs text-gray-500 mt-0.5">按客户汇总交易明细 · 关联客户管理模块</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              {[{ key: 'all', label: '全部' }, { key: '供应商', label: '供应商' }, { key: '客户', label: '客户' }].map(t => (
                <button key={t.key} onClick={() => setFilterType(t.key as typeof filterType)}
                  className={`px-4 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${filterType === t.key ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={handleExportPDF} disabled={exportingPDF}
              className="px-4 py-2 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg hover:shadow-md transition-all text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center">
              {exportingPDF ? <><i className="ri-loader-4-line mr-1.5 animate-spin"></i>导出中...</> : <><i className="ri-file-pdf-2-line mr-1.5"></i>导出PDF</>}
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* 内容 */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          <div ref={contentRef} className="space-y-6">
            <div className="text-center border-b-2 border-gray-800 pb-5">
              <h1 className="text-2xl font-bold text-gray-900 tracking-wider mb-2">客户往来统计表</h1>
              <div className="flex items-center justify-center space-x-6 text-sm text-gray-600 mt-3">
                <span>企业名称：<strong className="text-gray-800">丰收粮食贸易有限公司</strong></span>
                <span className="text-gray-300">|</span>
                <span>统计范围：<strong className="text-gray-800">{filterType === 'all' ? '全部客户' : filterType}</strong></span>
                <span className="text-gray-300">|</span>
                <span>填报日期：<strong className="text-gray-800">{new Date().toLocaleDateString('zh-CN')}</strong></span>
              </div>
            </div>

            {/* 汇总卡片 */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: '客户/供应商数', value: `${customerStats.length}`, unit: '家', color: 'bg-gray-50 border-gray-200', valColor: 'text-gray-800' },
                { label: '累计采购金额', value: `¥${(totals.purchaseAmt / 10000).toFixed(1)}万`, unit: '', color: 'bg-teal-50 border-teal-200', valColor: 'text-teal-700' },
                { label: '累计销售金额', value: `¥${(totals.salesAmt / 10000).toFixed(1)}万`, unit: '', color: 'bg-orange-50 border-orange-200', valColor: 'text-orange-600' },
                { label: '累计交易笔数', value: `${totals.totalCount}`, unit: '笔', color: 'bg-emerald-50 border-emerald-200', valColor: 'text-emerald-700' },
              ].map(item => (
                <div key={item.label} className={`${item.color} border rounded-xl p-3 text-center`}>
                  <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                  <div className={`text-lg font-bold ${item.valColor}`}>{item.value}{item.unit && <span className="text-xs font-normal ml-0.5">{item.unit}</span>}</div>
                </div>
              ))}
            </div>

            {/* 主表 */}
            {customerStats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-300 bg-gray-50 rounded-xl border border-gray-100">
                <i className="ri-inbox-line text-3xl mb-2"></i>
                <p className="text-sm text-gray-400">暂无{filterType === 'all' ? '' : filterType}交易数据</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-100">
                      {['序号','客户名称','类型','联系人','联系电话','地址','经营品种','采购量(吨)','采购金额(元)','销售量(吨)','销售金额(元)','交易总额(元)','交易笔数','最近交易'].map(h => (
                        <th key={h} className="border-b border-gray-200 px-2 py-3 text-gray-700 font-bold whitespace-nowrap text-center">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {customerStats.map((c, idx) => (
                      <tr key={c.name} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} hover:bg-teal-50/20 transition-colors`}>
                        <td className="px-2 py-2.5 text-center text-gray-400">{idx + 1}</td>
                        <td className="px-2 py-2.5 text-center font-semibold text-gray-800 whitespace-nowrap">{c.name}</td>
                        <td className="px-2 py-2.5 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.type === '供应商' ? 'bg-teal-100 text-teal-700' : 'bg-orange-100 text-orange-700'}`}>{c.type}</span>
                        </td>
                        <td className="px-2 py-2.5 text-center text-gray-600 whitespace-nowrap">{c.contact || '—'}</td>
                        <td className="px-2 py-2.5 text-center text-gray-600 whitespace-nowrap">{c.phone || '—'}</td>
                        <td className="px-2 py-2.5 text-center text-gray-500 max-w-[100px] truncate" title={c.address}>{c.address || '—'}</td>
                        <td className="px-2 py-2.5 text-center text-gray-600 whitespace-nowrap">{c.varietyList}</td>
                        <td className="px-2 py-2.5 text-right text-teal-600 font-medium">{c.purchaseQty > 0 ? c.purchaseQty.toLocaleString() : '—'}</td>
                        <td className="px-2 py-2.5 text-right text-gray-700">{c.purchaseAmt > 0 ? c.purchaseAmt.toLocaleString() : '—'}</td>
                        <td className="px-2 py-2.5 text-right text-orange-500 font-medium">{c.salesQty > 0 ? c.salesQty.toLocaleString() : '—'}</td>
                        <td className="px-2 py-2.5 text-right text-gray-700">{c.salesAmt > 0 ? c.salesAmt.toLocaleString() : '—'}</td>
                        <td className="px-2 py-2.5 text-right font-bold text-gray-800">{c.totalAmt.toLocaleString()}</td>
                        <td className="px-2 py-2.5 text-center text-gray-600">{c.totalCount}</td>
                        <td className="px-2 py-2.5 text-center text-gray-500 whitespace-nowrap">{c.lastDate}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      <td className="px-2 py-3 text-center text-gray-800" colSpan={7}>合计</td>
                      <td className="px-2 py-3 text-right text-teal-700">{totals.purchaseQty.toLocaleString()}</td>
                      <td className="px-2 py-3 text-right text-gray-800">{totals.purchaseAmt.toLocaleString()}</td>
                      <td className="px-2 py-3 text-right text-orange-600">{totals.salesQty.toLocaleString()}</td>
                      <td className="px-2 py-3 text-right text-gray-800">{totals.salesAmt.toLocaleString()}</td>
                      <td className="px-2 py-3 text-right text-gray-800">{totals.totalAmt.toLocaleString()}</td>
                      <td className="px-2 py-3 text-center text-gray-800">{totals.totalCount}</td>
                      <td className="px-2 py-3 text-center text-gray-400">—</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* 保密声明 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 leading-relaxed">
              <i className="ri-shield-keyhole-line mr-1"></i>
              <strong>保密声明：</strong>本统计表涉及客户商业信息和个人隐私，严格按照相关保密规定执行，未经授权不得向第三方披露。
            </div>

            {/* 签章 */}
            <div className="border-t-2 border-gray-800 pt-5 mt-2">
              <div className="grid grid-cols-3 gap-8 text-sm text-gray-700">
                <div><div className="font-medium text-gray-800 mb-5">制表人（签字）：</div><div className="border-b border-gray-400 pb-1"></div></div>
                <div><div className="font-medium text-gray-800 mb-5">企业负责人（签字）：</div><div className="border-b border-gray-400 pb-1"></div></div>
                <div><div className="font-medium text-gray-800 mb-5">（企业公章）</div><div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center"><span className="text-xs text-gray-400">公章位置</span></div></div>
              </div>
              <div className="text-right text-sm text-gray-600 mt-4">填报日期：{new Date().getFullYear()} 年 {new Date().getMonth() + 1} 月 {new Date().getDate()} 日</div>
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center text-sm text-gray-500">
            <i className="ri-information-line mr-1.5 text-teal-600"></i>
            共 {customerStats.length} 个{filterType === 'all' ? '客户/供应商' : filterType} · 数据来源：台账 + 客户管理
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-medium whitespace-nowrap cursor-pointer">关闭</button>
            <button onClick={handleExportPDF} disabled={exportingPDF}
              className="px-8 py-2.5 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:shadow-lg text-sm font-medium whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center">
              {exportingPDF ? <><i className="ri-loader-4-line mr-2 animate-spin"></i>导出中...</> : <><i className="ri-file-pdf-2-line mr-2"></i>导出PDF</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
